import { supabase } from "@/integrations/supabase/client";

export interface ClientKPIData {
  clientId: string;
  activities: {
    current: number;
    previous: number;
    color: 'gray' | 'yellow' | 'green' | 'red';
  };
  initiatives: {
    current: number;
    previous: number;
    colors: ('gray' | 'yellow' | 'green' | 'red')[];
  };
  proposals: {
    current: number;
    previous: number;
    colors: ('gray' | 'yellow' | 'green' | 'red')[];
  };
  business: {
    current: number;
    previous: number;
    color: 'gray' | 'green' | 'red';
  };
}

class ClientKPIService {
  private get182DaysAgo() {
    const date = new Date();
    date.setDate(date.getDate() - 182);
    return date;
  }

  private get182DaysAgoPreviousYear() {
    const date = new Date();
    date.setDate(date.getDate() - 182);
    date.setFullYear(date.getFullYear() - 1);
    return date;
  }

  private get182DaysAgoEndPreviousYear() {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date;
  }

  private getYearToDateStart() {
    const date = new Date();
    date.setMonth(0, 1); // January 1st of current year
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private getYearToDateStartPreviousYear() {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    date.setMonth(0, 1); // January 1st of previous year
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private getSameDatePreviousYear() {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date;
  }

  async getClientKPIs(clientIds: string[]): Promise<Record<string, ClientKPIData>> {
    if (clientIds.length === 0) return {};

    const now = new Date();
    const days182Ago = this.get182DaysAgo();
    const days182AgoPrevYear = this.get182DaysAgoPreviousYear();
    const days182AgoEndPrevYear = this.get182DaysAgoEndPreviousYear();

    // YTD dates for business KPI
    const ytdStart = this.getYearToDateStart();
    const ytdStartPrevYear = this.getYearToDateStartPreviousYear();
    const sameDatePrevYear = this.getSameDatePreviousYear();

    // Parallel fetch all required data
    const [
      activitiesData,
      initiativesData, 
      proposalsData,
      invoicesData
    ] = await Promise.all([
      this.fetchActivitiesKPI(clientIds, days182Ago, days182AgoPrevYear, days182AgoEndPrevYear),
      this.fetchInitiativesKPI(clientIds, days182Ago, days182AgoPrevYear, days182AgoEndPrevYear),
      this.fetchProposalsKPI(clientIds, days182Ago, days182AgoPrevYear, days182AgoEndPrevYear),
      this.fetchInvoicesKPI(clientIds, ytdStart, ytdStartPrevYear, sameDatePrevYear)
    ]);

    // Combine all KPIs
    const result: Record<string, ClientKPIData> = {};
    
    clientIds.forEach(clientId => {
      result[clientId] = {
        clientId,
        activities: activitiesData[clientId] || { current: 0, previous: 0, color: 'gray' },
        initiatives: initiativesData[clientId] || { current: 0, previous: 0, colors: ['gray'] },
        proposals: proposalsData[clientId] || { current: 0, previous: 0, colors: ['gray'] },
        business: invoicesData[clientId] || { current: 0, previous: 0, color: 'gray' }
      };
    });

    return result;
  }

  private async fetchActivitiesKPI(clientIds: string[], days182Ago: Date, days182AgoPrevYear: Date, days182AgoEndPrevYear: Date) {
    const { data, error } = await supabase
      .from('activities')
      .select('client_id, activity_date, type')
      .in('client_id', clientIds)
      .gte('activity_date', days182AgoPrevYear.toISOString())
      .lte('activity_date', new Date().toISOString());

    if (error) throw error;

    const result: Record<string, { current: number; previous: number; color: 'gray' | 'yellow' | 'green' | 'red' }> = {};

    clientIds.forEach(clientId => {
      const clientActivities = data?.filter(a => a.client_id === clientId) || [];
      
      // Count outbound activities (email, linkedin, etc.)
      const currentOutbound = clientActivities.filter(a => 
        new Date(a.activity_date) >= days182Ago && 
        ['email', 'linkedin', 'call', 'meeting', 'outreach'].includes(a.type?.toLowerCase())
      ).length;
      
      const previousOutbound = clientActivities.filter(a => 
        new Date(a.activity_date) >= days182AgoPrevYear && 
        new Date(a.activity_date) <= days182AgoEndPrevYear &&
        ['email', 'linkedin', 'call', 'meeting', 'outreach'].includes(a.type?.toLowerCase())
      ).length;

      // Color coding: Gray (0), Yellow (1-3), Green (4-6), Red (7+)
      let color: 'gray' | 'yellow' | 'green' | 'red' = 'gray';
      if (currentOutbound >= 7) color = 'red';
      else if (currentOutbound >= 4) color = 'green';
      else if (currentOutbound >= 1) color = 'yellow';

      result[clientId] = {
        current: currentOutbound,
        previous: previousOutbound,
        color
      };
    });

    return result;
  }

  private async fetchInitiativesKPI(clientIds: string[], days182Ago: Date, days182AgoPrevYear: Date, days182AgoEndPrevYear: Date) {
    // Get initiatives that have activities in the last 182 days
    const { data: initiativeActivities, error: activitiesError } = await supabase
      .from('activities')
      .select('client_id, initiative_id, activity_date, response_status')
      .in('client_id', clientIds)
      .not('initiative_id', 'is', null)
      .gte('activity_date', days182AgoPrevYear.toISOString())
      .lte('activity_date', new Date().toISOString());

    if (activitiesError) throw activitiesError;

    const result: Record<string, { current: number; previous: number; colors: ('gray' | 'yellow' | 'green' | 'red')[] }> = {};

    clientIds.forEach(clientId => {
      const clientInitiatives = initiativeActivities?.filter(a => a.client_id === clientId) || [];
      
      // Get unique initiatives for current and previous periods
      const currentInitiatives = [...new Set(clientInitiatives
        .filter(a => new Date(a.activity_date) >= days182Ago)
        .map(a => a.initiative_id)
      )];
      
      const previousInitiatives = [...new Set(clientInitiatives
        .filter(a => 
          new Date(a.activity_date) >= days182AgoPrevYear && 
          new Date(a.activity_date) <= days182AgoEndPrevYear
        )
        .map(a => a.initiative_id)
      )];

      // Determine colors based on response status
      const responseStatuses = clientInitiatives
        .filter(a => new Date(a.activity_date) >= days182Ago)
        .map(a => a.response_status)
        .filter(Boolean);

      const colors: ('gray' | 'yellow' | 'green' | 'red')[] = [];
      
      if (responseStatuses.includes('accepted')) colors.push('green');
      if (responseStatuses.includes('rejected')) colors.push('red');  
      if (responseStatuses.includes('interested')) colors.push('yellow');
      if (colors.length === 0 || responseStatuses.every(s => s === 'no_response')) colors.push('gray');

      result[clientId] = {
        current: currentInitiatives.length,
        previous: previousInitiatives.length,
        colors: colors.length > 0 ? colors : ['gray']
      };
    });

    return result;
  }

  private async fetchProposalsKPI(clientIds: string[], days182Ago: Date, days182AgoPrevYear: Date, days182AgoEndPrevYear: Date) {
    const { data, error } = await supabase
      .from('proposals')
      .select('client_id, sent_date, status')
      .in('client_id', clientIds)
      .not('sent_date', 'is', null)
      .gte('sent_date', days182AgoPrevYear.toISOString())
      .lte('sent_date', new Date().toISOString());

    if (error) throw error;

    const result: Record<string, { current: number; previous: number; colors: ('gray' | 'yellow' | 'green' | 'red')[] }> = {};

    clientIds.forEach(clientId => {
      const clientProposals = data?.filter(p => p.client_id === clientId) || [];
      
      const currentProposals = clientProposals.filter(p => 
        new Date(p.sent_date) >= days182Ago
      );
      
      const previousProposals = clientProposals.filter(p => 
        new Date(p.sent_date) >= days182AgoPrevYear && 
        new Date(p.sent_date) <= days182AgoEndPrevYear
      );

      // Determine colors based on proposal status
      const statuses = currentProposals.map(p => p.status);
      const colors: ('gray' | 'yellow' | 'green' | 'red')[] = [];
      
      if (statuses.includes('accepted')) colors.push('green');
      if (statuses.includes('rejected')) colors.push('red');
      if (statuses.includes('pending')) colors.push('yellow');
      if (colors.length === 0) colors.push('gray');

      result[clientId] = {
        current: currentProposals.length,
        previous: previousProposals.length,
        colors: colors.length > 0 ? colors : ['gray']
      };
    });

    return result;
  }

  private async fetchInvoicesKPI(clientIds: string[], ytdStart: Date, ytdStartPrevYear: Date, sameDatePrevYear: Date) {
    const { data, error } = await supabase
      .from('invoices')
      .select('client_id, issue_date, amount')
      .in('client_id', clientIds)
      .not('issue_date', 'is', null)
      .gte('issue_date', ytdStartPrevYear.toISOString())
      .lte('issue_date', new Date().toISOString());

    if (error) throw error;

    const result: Record<string, { current: number; previous: number; color: 'gray' | 'green' | 'red' }> = {};

    clientIds.forEach(clientId => {
      const clientInvoices = data?.filter(i => i.client_id === clientId) || [];
      
      // YTD current year: from Jan 1st current year to today
      const currentAmount = clientInvoices
        .filter(i => new Date(i.issue_date) >= ytdStart)
        .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
      
      // YTD previous year: from Jan 1st previous year to same date as today but in previous year
      const previousAmount = clientInvoices
        .filter(i => 
          new Date(i.issue_date) >= ytdStartPrevYear && 
          new Date(i.issue_date) <= sameDatePrevYear
        )
        .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);

      // Color coding: Gray (no invoices or equal), Red (current < previous), Green (current > previous)
      let color: 'gray' | 'green' | 'red' = 'gray';
      if (currentAmount === 0 && previousAmount === 0) {
        color = 'gray';
      } else if (currentAmount < previousAmount) {
        color = 'red';
      } else if (currentAmount > previousAmount) {
        color = 'green';  
      } else {
        color = 'gray'; // equal amounts
      }

      result[clientId] = {
        current: currentAmount,
        previous: previousAmount,
        color
      };
    });

    return result;
  }
}

export const clientKPIService = new ClientKPIService();